/**
 * The site-wide JSON-LD nodes: who WeAreQA is, the site itself, and a service.
 *
 * ONE DEFINITION OF THE COMPANY. The home page used to declare
 * `#organization` as an `Organization` and /about declared the same `@id` as a
 * `ProfessionalService` -- with a different description, a different `url`
 * (with and without the trailing slash) and a description ("AI-native quality
 * engineering") that appears nowhere on the page. Two nodes claiming one
 * identity with conflicting facts is exactly what a search engine cannot
 * reconcile, so both pages now emit this one.
 *
 * VISIBLE-TEXT RULE, inherited from design/04-SEO-SPEC.md: every property here
 * has matching text somewhere a visitor can read it. The description is the
 * footer blurb, which is on every page. Offices and phones are on /contact. The
 * four countries are on /about ("across the US, Canada, UK and Ireland").
 * `knowsAbout` names the service lines exactly as the site names them.
 *
 * Deliberately absent, because nothing on the site supports them: foundingDate,
 * legalName, a Clutch `sameAs` (the footer links it, but the profile could not
 * be verified), and any Review or AggregateRating.
 */
import { ORGANIZATION_ID, SITE_URL } from './site';

type JsonLd = Record<string, unknown>;

export function organizationJsonLd(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'WeAreQA',
    url: `${SITE_URL}/`,
    /*
     * A stable URL rather than the hashed build asset the header uses: a logo
     * in structured data is fetched and cached by search engines, and a
     * filename that changes with every build would break that. The wordmark on
     * transparency, because Google requires the logo to read correctly on white.
     */
    logo: `${SITE_URL}/logo.png`,
    description:
      'Quality engineering for teams shipping regulated software, and QA hiring with no recruitment fee.',
    email: 'hello@weareqa.com',
    /*
     * The locale-neutral linkedin.com host, not the nl.linkedin.com mirror the
     * page is often shared as: both resolve, but a localised host claims this
     * is the Dutch edition of the profile rather than the profile.
     */
    sameAs: ['https://www.linkedin.com/company/weareqa'],
    knowsAbout: [
      'Quality engineering',
      'AI-driven validation',
      'Test automation',
      'QA consulting',
      'QA hiring',
    ],
    areaServed: [
      { '@type': 'Country', name: 'United States' },
      { '@type': 'Country', name: 'Canada' },
      { '@type': 'Country', name: 'United Kingdom' },
      { '@type': 'Country', name: 'Ireland' },
    ],
    address: [
      {
        '@type': 'PostalAddress',
        streetAddress: 'Shevchenka 25 str.',
        addressLocality: 'Lviv',
        addressCountry: 'UA',
      },
      {
        '@type': 'PostalAddress',
        streetAddress: 'Mogilska str.',
        addressLocality: 'Krakow',
        addressCountry: 'PL',
      },
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+380936236393',
        contactType: 'sales',
        areaServed: 'UA',
        email: 'hello@weareqa.com',
      },
      {
        '@type': 'ContactPoint',
        telephone: '+48573831894',
        contactType: 'sales',
        areaServed: 'PL',
        email: 'hello@weareqa.com',
      },
    ],
  };
}

export function webSiteJsonLd(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    name: 'WeAreQA',
    url: `${SITE_URL}/`,
    publisher: { '@id': ORGANIZATION_ID },
  };
}

/**
 * A consulting service page.
 *
 * There is no Google rich result for `Service`; this is for machine
 * understanding only -- which company provides what, under which name. Kept to
 * properties the page states: its name and its description. No price, no
 * `areaServed` and no `offers`, because the service pages state none.
 */
export function serviceJsonLd(input: { path: string; name: string; description: string }): JsonLd {
  const url = `${SITE_URL}${input.path}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${url}#service`,
    name: input.name,
    serviceType: input.name,
    description: input.description,
    url,
    provider: { '@id': ORGANIZATION_ID },
  };
}
